/**
 * The docs' first page: what Morcast is, its key figures, why it exists, what it does in four
 * steps, what that changes for a brand and a creator, and the two parts it is made of (the
 * platform and the Morcast Payment Protocol). The statements follow the V1 specification
 * (docs/specification.md).
 */

import { Link } from "react-router";

import { styles } from "../../components/ui.js";
import { GuideSteps } from "../guides/parts.js";
import { CampaignFlow } from "./diagrams.js";
import { PROTOCOL_URL } from "./nav.js";
import { DocPage, DocSection, DocTable, KeyFacts, P, Points } from "./parts.js";

/** Where the market's size and the survey of marketers come from. */
const MARKET_SOURCE = "https://influencermarketinghub.com/influencer-marketing-statistics/";

/** The figures that define a campaign, each from the settlement rules. */
const keyFacts = [
  { value: "80%", label: "of what a brand pays goes to creators" },
  { value: "20%", label: "is Morcast's fee, taken only on verified reach" },
  { value: "100%", label: "of the budget not earned returns to the brand" },
  { value: "1%", label: "of the target is the least reach a creator needs to count" },
  { value: "5 to 10 days", label: "from a campaign's end to its settlement" },
  { value: "USDC or MOR", label: "one token per campaign, on Base" },
];

const steps = [
  {
    title: "The brand locks a budget",
    text: "It sets a budget and a target, such as 1,000,000 views, and deposits the budget into an escrow contract on Base, in USDC or MOR.",
  },
  {
    title: "Creators post",
    text: "Any creator with an X account or a YouTube channel can take part. There is no follower minimum and no application.",
  },
  {
    title: "Morcast verifies",
    text: "After the campaign ends, Morcast reads each post's organic reach from the platform, with the creator's permission, and checks the post against the brand's brief. Paid promotion never counts.",
  },
  {
    title: "The escrow settles",
    text: "The brand pays for the reach delivered and never more than its budget. Creators receive 80% of that, Morcast receives 20%, and the rest of the budget returns to the brand.",
  },
];

export function Overview() {
  return (
    <DocPage
      group="Morcast"
      title="Overview"
      documentTitle="Docs"
      lead="Morcast lets a brand pay creators only for the reach their content really delivers. The budget is locked in an escrow on Base before anyone posts, every result is published for anyone to check, and what is not earned returns to the brand."
    >
      <DocSection id="key-facts" title="Key facts">
        <KeyFacts facts={keyFacts} />
      </DocSection>

      <DocSection id="why" title="Why Morcast exists">
        <P>Brands pay creators for promises, and nobody can check what was delivered.</P>
        <Points
          items={[
            {
              lead: "The price follows the follower count.",
              text: "A follower count does not say how many people will see one post. A brand pays the same whether a post reaches 2,000 people or 2 million.",
            },
            {
              lead: "The real numbers are private.",
              text: "Only the creator sees a post's analytics. Brands rely on screenshots and reports from the party being paid.",
            },
            {
              lead: "Numbers can be bought.",
              text: (
                <>
                  Followers, likes and comments are for sale, and paid promotion inflates views. In
                  a 2026 survey of more than 600 marketers, fake or bot followers made up 56.5% of
                  the fraud and quality problems they reported (
                  <a href={MARKET_SOURCE} rel="noopener" className={styles.link}>
                    Influencer Marketing Hub
                  </a>
                  ).
                </>
              ),
            },
            {
              lead: "Creators carry risk too.",
              text: "Deals go to large accounts, and payment comes after the work, on the brand's word.",
            },
          ]}
        />
        <P>
          The money at stake is large. Influencer marketing reached an estimated USD 32.55 billion
          in 2025 (
          <a href={MARKET_SOURCE} rel="noopener" className={styles.link}>
            Influencer Marketing Hub
          </a>
          ).
        </P>
        <P>
          Morcast comes from one fact. X and YouTube already measure the real reach of every post,
          and its creator can let a neutral party read it. Once reach is read at the source, a brand
          can pay for results in place of promises.
        </P>
      </DocSection>

      <DocSection id="what" title="What Morcast does">
        <P>Morcast turns a campaign into a locked budget that pays only for verified reach.</P>
        <GuideSteps steps={steps} />
        <CampaignFlow />
      </DocSection>

      <DocSection id="changes" title="What this changes">
        <DocTable
          caption="Creator campaigns today and with Morcast"
          head={["Today", "With Morcast"]}
          rows={[
            [
              "A brand pays before it knows the result",
              "It pays only for verified reach, up to its budget",
            ],
            [
              "Reach is reported by the party being paid",
              "Reach is read from the platform's own analytics, organic only",
            ],
            [
              "A creator is paid later, on the brand's word",
              "The budget is locked before anyone posts, and payouts are claimed from the escrow",
            ],
            ["Deals go to large accounts", "Any creator takes part and earns by reach"],
            [
              "Nobody can audit the numbers",
              "Every result is published with its evidence, for anyone to recompute",
            ],
          ]}
        />
      </DocSection>

      <DocSection id="parts" title="A platform and a protocol">
        <P>Morcast is made of two parts, and each has its own pages in these docs.</P>
        <Points
          items={[
            {
              lead: "The platform.",
              text: (
                <>
                  This site is where brands request campaigns, creators link their accounts and
                  submit their posts, and every result is published. Its{" "}
                  <Link to="/docs/business-model" className={styles.link}>
                    business model
                  </Link>{" "}
                  is one fee on verified reach.
                </>
              ),
            },
            {
              lead: "The Morcast Payment Protocol.",
              text: (
                <>
                  The escrow contract on Base and the open rules that turn a published result into
                  payouts. It is{" "}
                  <a href={PROTOCOL_URL} rel="noopener" className={styles.link}>
                    open source
                  </a>
                  , and{" "}
                  <Link to="/docs/protocol" className={styles.link}>
                    how it works
                  </Link>{" "}
                  is set out step by step.
                </>
              ),
            },
          ]}
        />
      </DocSection>
    </DocPage>
  );
}
